import {defineStore} from 'pinia'
import type {OutputUavMission, OutputUavMissionNote, OutputUavMissionShootdown, GetUavMissionsModeEnum} from '~/models'

interface UavMissionNotification {
  type: string
  action: 'created' | 'updated' | 'deleted'
  missionId: number
  payload?: OutputUavMission
}

// Знімок для відкату оптимістичної зміни статусу: разом зі статусом гасне й ручна корекція
// позиції борта, тож відкочувати треба обидва.
interface UavMissionStatusSnapshot {
  status?: string
  statusChangedAt?: string
  airborneAt?: string
  posOverrideDist?: number
  posOverrideDir?: number
  posOverrideAt?: string
}

// Знімок ручної корекції позиції — для відкату, якщо запит перетягування не пройшов.
interface UavMissionPositionSnapshot {
  posOverrideDist?: number
  posOverrideDir?: number
  posOverrideAt?: string
}

interface UavMissionNoteNotification {
  type: string
  action: 'created'
  missionId: number
  payload?: OutputUavMissionNote
}

// Фази, у яких борт у повітрі — за ними живе годинник наліту (airborneAt) при оптимістичних
// змінах статусу. Дзеркало services.uavAirborneStatuses на бекенді.
const AIRBORNE_STATUSES = new Set(['airborne_outbound', 'beyond_contact_line', 'airborne_inbound'])

// Доки борт у повітрі тримає заявку на борді. Дзеркало services.UavAirborneBoardWindow.
const AIRBORNE_BOARD_WINDOW_MS = 24 * 60 * 60 * 1000

// Резерв, доки заявка ще висить на борді після завершення — за часом (після active_until)
// або кнопкою (після переходу в «Земля»). Дзеркало services.UavBoardGrace.
const BOARD_GRACE_MS = 60 * 60 * 1000

// Єдиний порядок для HTTP і realtime: новіші заявки завжди зверху.
// Не покладаємось лише на ORDER BY бекенда, бо Centrifugo патчить масив без нового GET.
function newestMissionsFirst(items: OutputUavMission[]): OutputUavMission[] {
  return [...items].sort((a, b) => (b.id ?? 0) - (a.id ?? 0))
}

// Дата "сьогодні" у київському часі — тим самим поясом міряє межу активності бекенд
// (GetUavMissionsListHandler), тож локальний час машини чергового на результат не впливає.
function kyivToday(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Kyiv', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date())
}

// Точна копія умови mode=active з GetUavMissionsListHandler. Потрібна тому, що Centrifugo шле
// подію про БУДЬ-ЯКУ заявку (нотатка у треді торкає й давно протерміновану), а вставляти таку
// в дашборд не можна: вона блимала б у списку зі звуком до наступного бекап-перезавантаження.
//
//	борт у повітрі        -> активна, поки airborne_at не старший за добу (реальний політ
//	                         довший за заявлене вікно; стеля — проти "зомбі" без доповіді)
//	щойно сів             -> активна ще годину після переходу в «Земля», навіть якщо вікно
//	                         вже спливло: картка не має гаснути в мить доповіді про посадку
//	майбутні (flight_date > today)                           -> не тут, а в mode=upcoming
//	active_until є        -> активна, поки active_until > now-резерв
//	active_until немає    -> активна, поки flight_date (або дата подачі) >= today
function isMissionActive(m: OutputUavMission): boolean {
  if (m.airborneAt) {
    const at = Date.parse(m.airborneAt)
    if (Number.isFinite(at) && Date.now() - at < AIRBORNE_BOARD_WINDOW_MS) return true
  }

  if (m.status === 'landed' && m.statusChangedAt) {
    const at = Date.parse(m.statusChangedAt)
    if (Number.isFinite(at) && Date.now() - at < BOARD_GRACE_MS) return true
  }

  const today = kyivToday()
  const flightDate = m.flightDate || ''
  if (flightDate && flightDate > today) return false

  if (m.activeUntil) {
    return new Date(m.activeUntil).getTime() > Date.now() - BOARD_GRACE_MS
  }

  const effectiveDate = flightDate
      || (m.createdAt ? new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Kyiv', year: 'numeric', month: '2-digit', day: '2-digit',
      }).format(new Date(m.createdAt)) : '')
  return !effectiveDate || effectiveDate >= today
}

export const useUavMissionsStore = defineStore('uavMissions', {
  state: () => ({
    missions: [] as OutputUavMission[],
    notesByMission: {} as Record<number, OutputUavMissionNote[]>,
    notesLoaded: {} as Record<number, boolean>,
    // Кількість нових нотаток (з елемента/треду), що прилетіли по Centrifugo, поки тред
    // цієї заявки був згорнутий. Живе лише в пам'яті вкладки — це індикатор "подивись сюди",
    // а не персистентний unread-стан як у чаті.
    unreadNotes: {} as Record<number, number>,
    // Які треди зараз розгорнуті — нотатки, що приходять у відкритий тред, юзер бачить одразу,
    // тож індикатор для них не піднімаємо.
    openThreads: {} as Record<number, boolean>,
    // id нотаток, які вже приходили по Centrifugo в цій вкладці. Потрібні, бо ту саму нотатку
    // ми отримуємо більш ніж один раз: спільна dev-БД шле pg_notify ВСІМ підписаним бекендам
    // (локальний + задеплоєний), і кожен публікує в Centrifugo свою копію. Для 'uav_mission' це
    // непомітно (заміна об'єкта ідемпотентна), а інкрементний лічильник давав би +2 за одне
    // повідомлення. Дедуп по id тримаємо незалежно від того, чи завантажений тред.
    seenNoteIds: [] as number[],
    // Остання нотатка, що прилетіла по Centrifugo, по кожній заявці — джерело для
    // короткочасної хмаринки біля літачка на мапі (DispatchMap). at (мітка часу приходу)
    // потрібна саме як тригер: мапа показує лише записи, новіші за вже показані.
    noteFlashes: {} as Record<number, {id?: number, body: string, authorName: string, at: number}>,
  }),
  actions: {
    // mode/search/silent — ті самі параметри, що приймає GET /api/uav-missions.
    async load(params: {mode?: GetUavMissionsModeEnum, search?: string, silent?: boolean} = {}) {
      const response = await useApi({silent: params.silent}).getUavMissionsRaw({
        mode: params.mode,
        search: params.search,
        limit: 1000,
        offset: 0,
        sortBy: 'id',
        orderBy: 'desc',
      })
      this.missions = newestMissionsFirst(await response.value())

      // Лічильники непрочитаних беремо з бекенду (uav_mission_note_reads), інакше після F5
      // маркер гаснув би на нотатках, які користувач так і не відкривав. Відкриті зараз треди
      // не чіпаємо — там усе вже прочитане, і серверне число прийшло б із запізненням.
      for (const mission of this.missions) {
        if (!mission.id || this.openThreads[mission.id]) continue
        this.unreadNotes[mission.id] = mission.unreadNotesCount ?? 0
      }
    },

    // Оптимістичний локальний патч статусу (без запиту) — для миттєвої реакції UI
    // (підсвітка кнопки, переміщення літака на мапі) до відповіді сервера.
    // statusChangedAt виставляємо тут же (той самий "зараз", що напише сервер) — це прибирає
    // потребу в подальшому silent reloadList, який раніше підміняв об'єкт місії ще раз і викликав
    // видимий "перемальовується" ефект на картці (StatusButtons більше не смикає reloadList).
    // Повертає попередній статус+час+ручну корекцію — використовується для відкату при помилці.
    //
    // Ручну корекцію позиції ГАСИМО разом зі статусом — рівно як ChangeUavMissionStatusHandler
    // без keepPosition. Без цього борт після «Повернення» стартував не з крайньої точки
    // маршруту, а з місця, куди його колись перетягнули: сервер override уже стер, а
    // локальний стор ще ні (reloadList тут навмисно не робиться).
    patchMissionStatus(missionId: number, status: string): UavMissionStatusSnapshot | undefined {
      const idx = this.missions.findIndex(m => m.id === missionId)
      if (idx < 0) return undefined
      const previous: UavMissionStatusSnapshot = {
        status: this.missions[idx].status,
        statusChangedAt: this.missions[idx].statusChangedAt,
        airborneAt: this.missions[idx].airborneAt,
        posOverrideDist: this.missions[idx].posOverrideDist,
        posOverrideDir: this.missions[idx].posOverrideDir,
        posOverrideAt: this.missions[idx].posOverrideAt,
      }
      const now = new Date().toISOString()
      this.missions[idx] = {
        ...this.missions[idx],
        status,
        statusChangedAt: now,
        // Годинник наліту (ліміт ТТХ) — дзеркало services.AirborneAtForStatusChange:
        // стартує на першому підйомі, переживає зміни фаз, гасне на землі. Без цього
        // лічильник ще секунду висів би на картці борта, який щойно сів.
        airborneAt: AIRBORNE_STATUSES.has(status)
            ? (this.missions[idx].airborneAt || now)
            : undefined,
        posOverrideDist: undefined,
        posOverrideDir: undefined,
        posOverrideAt: undefined,
      }
      return previous
    },

    // Оптимістичний локальний патч ручної корекції позиції (перетягування борта на мапі).
    // Без нього диспетчерська мусила б перезавантажувати список, щоб побачити власне
    // перетягування, — і та відповідь, приходячи ПІСЛЯ наступної зміни статусу, воскрешала б
    // стару позицію: борт мерехтів між точкою перетягування та якорем нової фази.
    patchMissionPositionOverride(missionId: number, dist: number, dir: number): UavMissionPositionSnapshot | undefined {
      const idx = this.missions.findIndex(m => m.id === missionId)
      if (idx < 0) return undefined
      const previous: UavMissionPositionSnapshot = {
        posOverrideDist: this.missions[idx].posOverrideDist,
        posOverrideDir: this.missions[idx].posOverrideDir,
        posOverrideAt: this.missions[idx].posOverrideAt,
      }
      this.missions[idx] = {
        ...this.missions[idx],
        posOverrideDist: dist,
        posOverrideDir: dir,
        // Той самий «зараз», що напише сервер: від цього моменту анімація рахує рух борта далі.
        posOverrideAt: new Date().toISOString(),
      }
      return previous
    },

    // Відкат після помилки запиту перетягування.
    restoreMissionPositionOverride(missionId: number, previous: UavMissionPositionSnapshot) {
      const idx = this.missions.findIndex(m => m.id === missionId)
      if (idx < 0) return
      this.missions[idx] = {...this.missions[idx], ...previous}
    },

    // Відкат після помилки запиту зміни статусу — повертає status+statusChangedAt
    // та ручну корекцію до знімку, знятого перед оптимістичним патчем.
    restoreMissionStatus(missionId: number, previous: UavMissionStatusSnapshot) {
      const idx = this.missions.findIndex(m => m.id === missionId)
      if (idx < 0) return
      this.missions[idx] = {...this.missions[idx], ...previous}
    },

    // Оптимістичне зняття тривоги РЕБ (кнопка на картці — екіпаж доповів, що подавлення
    // більше немає). Пульсація борта на мапі гасне одразу, запит іде у фоні.
    // Повертає попередній момент доповіді — для відкату при помилці запиту.
    clearMissionEwReport(missionId: number): {ewReportedAt?: string} | undefined {
      const idx = this.missions.findIndex(m => m.id === missionId)
      if (idx < 0) return undefined
      const previous = {ewReportedAt: this.missions[idx].ewReportedAt}
      this.missions[idx] = {...this.missions[idx], ewReportedAt: undefined}
      return previous
    },

    // Відкат після помилки запиту зняття тривоги РЕБ.
    restoreMissionEwReport(missionId: number, previous: {ewReportedAt?: string}) {
      const idx = this.missions.findIndex(m => m.id === missionId)
      if (idx < 0) return
      this.missions[idx] = {...this.missions[idx], ...previous}
    },

    // Оптимістичний локальний append нового запису в журнал збитих бортів (+ статус
    // awaiting_launch, як в AddUavMissionShootdownHandler: екіпаж піднімає резервний борт і
    // продовжує ту саму заявку) — миттєва реакція UI до відповіді сервера.
    // Повертає попередній список+статус — використовується для відкату при помилці запиту.
    addMissionShootdown(missionId: number, coords: string): {shootdowns: OutputUavMissionShootdown[], status?: string, airborneAt?: string} | undefined {
      const idx = this.missions.findIndex(m => m.id === missionId)
      if (idx < 0) return undefined
      const previous = {
        shootdowns: this.missions[idx].shootdowns ?? [],
        status: this.missions[idx].status,
        airborneAt: this.missions[idx].airborneAt,
      }
      const entry: OutputUavMissionShootdown = {
        seq: previous.shootdowns.length + 1,
        coords,
        occurredAt: new Date().toISOString(),
        reportedBy: 0,
        reportedByName: '',
      }
      // Борта, з якого йшов наліт, більше немає — годинник ТТХ гасне разом зі статусом
      // (як в AddUavMissionShootdownHandler). Резервний борт підніме його заново.
      this.missions[idx] = {
        ...this.missions[idx],
        shootdowns: [...previous.shootdowns, entry],
        status: 'awaiting_launch',
        airborneAt: undefined,
      }
      return previous
    },

    // Оптимістичне видалення останнього запису журналу (undo помилкового внесення).
    // Статус НЕ відкочується (як в DeleteLastUavMissionShootdownHandler).
    removeLastMissionShootdown(missionId: number): OutputUavMissionShootdown[] | undefined {
      const idx = this.missions.findIndex(m => m.id === missionId)
      if (idx < 0) return undefined
      const previous = this.missions[idx].shootdowns ?? []
      this.missions[idx] = {...this.missions[idx], shootdowns: previous.slice(0, -1)}
      return previous
    },

    // Відкат після помилки запиту addMissionShootdown/removeLastMissionShootdown —
    // повертає журнал (і за потреби статус) до знятого раніше знімку.
    restoreMissionShootdowns(missionId: number, shootdowns: OutputUavMissionShootdown[], status?: string, airborneAt?: string) {
      const idx = this.missions.findIndex(m => m.id === missionId)
      if (idx < 0) return
      // airborneAt повертаємо лише разом зі статусом: undo видалення запису журналу статус не
      // чіпає (DeleteLastUavMissionShootdownHandler), тож і годинник наліту чіпати нічого.
      this.missions[idx] = {...this.missions[idx], shootdowns, ...(status ? {status, airborneAt} : {})}
    },

    // Тред розгорнули/згорнули. Розгортання гасить індикатор одразу (локально) і фіксує
    // позначку "прочитано" на сервері — щоб маркер не повернувся після перезавантаження.
    setThreadOpen(missionId: number, open: boolean) {
      this.openThreads[missionId] = open
      if (!open) return

      this.unreadNotes[missionId] = 0
      this.markNotesRead(missionId)
    },

    // Позначка "прочитано" — fire-and-forget: маркер уже згас локально, і збій запиту не має
    // ні ламати розгортання треду, ні кидати помилку в UI (silent). Найгірший наслідок —
    // маркер повернеться після F5.
    async markNotesRead(missionId: number) {
      try {
        await useApi({silent: true}).markUavMissionNotesRead({id: missionId})
      } catch {
        // навмисно проковтуємо
      }
    },

    async loadNotes(missionId: number) {
      const notes = await useApi().getUavMissionNotes({id: missionId})
      this.notesByMission[missionId] = notes
      this.notesLoaded[missionId] = true
    },

    handleCentrifugeNotification(message: UavMissionNotification | UavMissionNoteNotification) {
      if (!message) return

      if (message.type === 'uav_mission') {
        const {action, missionId, payload} = message as UavMissionNotification
        switch (action) {
          case 'created':
          case 'updated':
            if (payload) {
              // Заявка вже в історії (вікно + резерв минули) — у дашборді їй не місце,
              // навіть якщо її щойно торкнулись (нотатка у треді, зміна статусу).
              if (!isMissionActive(payload)) {
                this.missions = this.missions.filter(m => m.id !== missionId)
                return
              }
              const idx = this.missions.findIndex(m => m.id === missionId)
              if (idx >= 0) {
                const updated = [...this.missions]
                // Толерантність до мішаних версій бекенду під час rollout: спільна dev-БД
                // (render.com) шле pg_notify ВСІМ підписаним бекендам, і старий задеплоєний
                // інстанс без фічі shootdowns транслює payload, де поля просто немає (undefined,
                // не []). Новий бекенд завжди шле масив (хай і порожній). Тож undefined трактуємо
                // як "відправник не знає про фічу" і зберігаємо локальний журнал — інакше старі
                // broadcast'и затирали б shootdowns у нуль, і chip блимав би on/off (toggle).
                // Те саме з uavPlatformIcon: старий інстанс шле заявку без силуету, і борт на мапі
                // перемикався б літак ↔ квадрокоптер з кожним broadcast'ом.
                const current = this.missions[idx]
                updated[idx] = {
                  ...payload,
                  shootdowns: payload.shootdowns === undefined ? current.shootdowns : payload.shootdowns,
                  uavPlatformIcon: payload.uavPlatformIcon === undefined ? current.uavPlatformIcon : payload.uavPlatformIcon,
                }
                this.missions = newestMissionsFirst(updated)
              } else {
                this.missions = newestMissionsFirst([...this.missions, payload])
              }
            }
            break
          case 'deleted':
            this.missions = this.missions.filter(m => m.id !== missionId)
            break
        }
        return
      }

      if (message.type === 'uav_mission_note') {
        const {missionId, payload} = message as UavMissionNoteNotification
        if (!payload) return

        const notes = this.notesByMission[missionId] || []
        // Дедуплікація: та сама нотатка приходить кілька разів (кілька бекендів на спільній БД),
        // плюс власна нотатка (щойно створена через addNote) прилітає і сюди. Ні в список,
        // ні в лічильник її вдруге не пускаємо.
        if (payload.id !== undefined) {
          if (this.seenNoteIds.includes(payload.id)) return
          // Вікно пам'яті обмежене — вкладка може жити добами, список не має рости без меж.
          this.seenNoteIds = [...this.seenNoteIds.slice(-499), payload.id]
        }
        if (this.notesLoaded[missionId] && notes.some(n => n.id === payload.id)) return

        // Звук — разом із хмаринкою, після дедупу: черговий може дивитись у сусіднє вікно,
        // а доповідь екіпажу не має чекати, доки він поверне погляд на мапу. Власну нотатку
        // (щойно надіслану з цієї ж вкладки) не озвучуємо — вона й так перед очима.
        // Звучимо лише за заявки, які зараз у дашборді: доповідь у треді протермінованої
        // (вже в історії) заявки — не привід смикати чергового.
        const inDashboard = this.missions.some(m => m.id === missionId)
        if (inDashboard && (payload.authorId == null || payload.authorId !== useAuth().user.value?.id)) {
          useUavNoteSound().playNoteSound()
        }

        // Хмаринка біля літачка — після дедупу, щоб одна й та сама нотатка не блимала двічі.
        // Ставимо незалежно від стану треду: на мапі чергового відкритої лівої панелі може
        // й не бути в полі зору.
        this.noteFlashes[missionId] = {
          id: payload.id,
          body: payload.body ?? '',
          authorName: payload.authorName ?? '',
          at: Date.now(),
        }

        // Індикатор піднімаємо навіть якщо тред ще жодного разу не відкривали (нотаток у
        // пам'яті немає) — саме це і є "нове повідомлення з елемента" на згорнутій картці.
        if (this.openThreads[missionId]) {
          // Тред відкритий — нотатку видно одразу, тож зсуваємо серверну позначку вперед.
          // Інакше після F5 вона повернулась би як непрочитана.
          this.markNotesRead(missionId)
        } else {
          this.unreadNotes[missionId] = (this.unreadNotes[missionId] ?? 0) + 1
        }

        // Тред не розкривали — не тримаємо нотатки в пам'яті, вони підвантажаться при toggle().
        if (!this.notesLoaded[missionId]) return
        this.notesByMission[missionId] = [...notes, payload]
      }
    },
  },
})
